import React, { useState, useEffect } from "react";
import { useContext } from "react";
import { CanvasContext } from "../../../contexts/CanvasContext";

const OutputComponent = ({ output = {} }) => {
  const { hoveredDivision, files } = useContext(CanvasContext);

  const isHovered = output.id === hoveredDivision;
  const [isDivHovered, setIsDivHovered] = useState(false);
  const [isDivFocused, setIsDivFocused] = useState(false);
  const [isDivActive, setIsDivActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = output;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = output;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = output;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = output;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (output.backgroundImage && output.backgroundImage.startsWith("./")) {
        const imageName = output.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(output.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [output.backgroundImage, files]);

  const focusedStyles =
    isDivFocused && output.pseudoClass === "focus"
      ? {
          // width: output.focusedwidth || output.width || "200px",
          // height: output.focusedheight || output.height || "auto",

          padding: output.focusedpadding || output.padding || "0",
          paddingTop:
            output.focusedpaddingTop || output.focusedpadding || "3px",
          paddingRight:
            output.focusedpaddingRight || output.focusedpadding || "3px",
          paddingBottom:
            output.focusedpaddingBottom || output.focusedpadding || "3px",
          paddingLeft:
            output.focusedpaddingLeft || output.focusedpadding || "3px",
          margin: output.focusedmargin || output.margin || "0px",
          marginTop: output.focusedmarginTop || output.focusedmargin || "0px",
          marginRight:
            output.focusedmarginRight || output.focusedmargin || "0px",
          marginBottom:
            output.focusedmarginBottom || output.focusedmargin || "0px",
          marginLeft: output.focusedmarginLeft || output.focusedmargin || "8px",

          borderTop:
            output.focusedborderTop ||
            output.focusedborder ||
            output.border ||
            "1px solid #000000",
          borderRight:
            output.focusedborderRight ||
            output.focusedborder ||
            output.border ||
            "1px solid #000000",
          borderBottom:
            output.focusedborderBottom ||
            output.focusedborder ||
            output.border ||
            "1px solid #000000",
          borderLeft:
            output.focusedborderLeft ||
            output.focusedborder ||
            output.border ||
            "1px solid #000000",
          borderSapcing:
            output.focusedborderSpacing || output.borderSpacing || "0px",
          borderCollapse:
            output.focusedborderCollapse || output.borderCollapse || "0px",
          borderRadius:
            output.focusedborderRadius || output.borderRadius || "0",
          outline: output.focusedoutline || output.outline || "none",

          position: output.focusedposition || output.position || "relative",
          top: output.focusedtop || output.top || "auto",
          right: output.focusedright || output.right || "auto",
          bottom: output.focusedbottom || output.bottom || "auto",
          left: output.focusedleft || output.left || "auto",
          zIndex: output.focusedzIndex || output.zIndex || "auto",

          color: output.focusedcolor || output.color || "#000000",
          backgroundColor:
            output.focusedbackgroundColor ||
            output.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (output.focusedbackgroundImage) {
              if (output.focusedbackgroundImage.startsWith("./")) {
                const imageName = output.focusedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${output.focusedbackgroundImage})`;
            }
            if (output.backgroundImage) {
              if (output.backgroundImage.startsWith("./")) {
                const imageName = output.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${output.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            output.focusedbackgroundPosition ||
            output.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            output.focusedbackgroundSize || output.backgroundSize || "auto",
          backgroundRepeat:
            output.focusedbackgroundRepeat ||
            output.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            output.focusedbackgroundAttachment ||
            output.backgroundAttachment ||
            "scroll",

          boxShadow: output.focusedboxShadow || output.boxShadow || "none",
          opacity: output.focusedOpacity || output.opacity || 1,
          display: output.focuseddisplay || output.display || "inline",

          fontFamily:
            output.focusedfontFamily || output.fontFamily || "inherit",
          fontSize: output.focusedfontSize || output.fontSize || "inherit",
          fontWeight: output.focusedfontWeight || output.fontWeight || "normal",
          fontStyle: output.focusedfontStyle || output.fontStyle || "normal",
          fontVariant:
            output.focusedfontVariant || output.fontVariant || "normal",
          lineHeight: output.focusedlineHeight || output.lineHeight || "normal",
          textShadow: output.focusedtextShadow || output.textShadow || "none",
          textAlign: output.focusedtextAlign || output.textAlign || "left",
          verticalAlign:
            output.focusedverticalAlign || output.verticalAlign || "baseline",
          textDecoration:
            output.focusedtextDecoration || output.textDecoration || "none",
          textTransform:
            output.focusedtextTransform || output.textTransform || "none",
          letterSpacing:
            output.focusedletterSpacing || output.letterSpacing || "normal",
          wordSpacing:
            output.focusedwordSpacing || output.wordSpacing || "normal",
          textIndent: output.focusedtextIndent || output.textIndent || "0",
          whiteSpace: output.focusedwhiteSpace || output.whiteSpace || "normal",
          textOverflow:
            output.focusedtextOverflow || output.textOverflow || "clip",
          direction: output.focuseddirection || output.direction || "ltr",
          unicodeBidi:
            output.focusedunicodeBidi || output.unicodeBidi || "normal",
          writingMode:
            output.focusedwritingMode || output.writingMode || "horizontal-tb",

          cursor: output.focusedcursor || output.cursor || "default",
          transform: output.focusedtransform || output.transform || "none",
          transition: output.focusedtransition || output.transition || "none",
        }
      : {};
  const hoverStyles =
    isDivHovered && output.pseudoClass === "hover"
      ? {
          // width: output.hoveredwidth || output.width || "200px",
          // height: output.hoveredheight || output.height || "auto",

          padding: output.hoveredpadding || output.padding || "0",
          paddingTop:
            output.hoveredpaddingTop || output.hoveredpadding || "3px",
          paddingRight:
            output.hoveredpaddingRight || output.hoveredpadding || "3px",
          paddingBottom:
            output.hoveredpaddingBottom || output.hoveredpadding || "3px",
          paddingLeft:
            output.hoveredpaddingLeft || output.hoveredpadding || "3px",
          margin: output.hoveredmargin || output.margin || "0px",
          marginTop: output.hoveredmarginTop || output.hoveredmargin || "0px",
          marginRight:
            output.hoveredmarginRight || output.hoveredmargin || "0px",
          marginBottom:
            output.hoveredmarginBottom || output.hoveredmargin || "0px",
          marginLeft: output.hoveredmarginLeft || output.hoveredmargin || "8px",

          borderTop:
            output.hoveredborderTop ||
            output.hoveredborder ||
            output.border ||
            "1px solid #000000",
          borderRight:
            output.hoveredborderRight ||
            output.hoveredborder ||
            output.border ||
            "1px solid #000000",
          borderBottom:
            output.hoveredborderBottom ||
            output.hoveredborder ||
            output.border ||
            "1px solid #000000",
          borderLeft:
            output.hoveredborderLeft ||
            output.hoveredborder ||
            output.border ||
            "1px solid #000000",
          borderSapcing:
            output.hoveredborderSpacing || output.borderSpacing || "0px",
          borderCollapse:
            output.hoveredborderCollapse || output.borderCollapse || "0px",
          borderRadius:
            output.hoveredborderRadius || output.borderRadius || "0",
          outline: output.hoveredoutline || output.outline || "none",

          position: output.hoveredposition || output.position || "relative",
          top: output.hoveredtop || output.top || "auto",
          right: output.hoveredright || output.right || "auto",
          bottom: output.hoveredbottom || output.bottom || "auto",
          left: output.hoveredleft || output.left || "auto",
          zIndex: output.hoveredzIndex || output.zIndex || "auto",

          color: output.hoveredcolor || output.color || "#000000",
          backgroundColor:
            output.hoveredbackgroundColor ||
            output.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (output.hoveredbackgroundImage) {
              if (output.hoveredbackgroundImage.startsWith("./")) {
                const imageName = output.hoveredbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${output.hoveredbackgroundImage})`;
            }
            if (output.backgroundImage) {
              if (output.backgroundImage.startsWith("./")) {
                const imageName = output.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${output.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            output.hoveredbackgroundPosition ||
            output.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            output.hoveredbackgroundSize || output.backgroundSize || "auto",
          backgroundRepeat:
            output.hoveredbackgroundRepeat ||
            output.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            output.hoveredbackgroundAttachment ||
            output.backgroundAttachment ||
            "scroll",

          boxShadow: output.hoveredboxShadow || output.boxShadow || "none",
          opacity: output.hoveredOpacity || output.opacity || 1,
          display: output.hovereddisplay || output.display || "inline",

          fontFamily:
            output.hoveredfontFamily || output.fontFamily || "inherit",
          fontSize: output.hoveredfontSize || output.fontSize || "inherit",
          fontWeight: output.hoveredfontWeight || output.fontWeight || "normal",
          fontStyle: output.hoveredfontStyle || output.fontStyle || "normal",
          fontVariant:
            output.hoveredfontVariant || output.fontVariant || "normal",
          lineHeight: output.hoveredlineHeight || output.lineHeight || "normal",
          textShadow: output.hoveredtextShadow || output.textShadow || "none",
          textAlign: output.hoveredtextAlign || output.textAlign || "left",
          verticalAlign:
            output.hoveredverticalAlign || output.verticalAlign || "baseline",
          textDecoration:
            output.hoveredtextDecoration || output.textDecoration || "none",
          textTransform:
            output.hoveredtextTransform || output.textTransform || "none",
          letterSpacing:
            output.hoveredletterSpacing || output.letterSpacing || "normal",
          wordSpacing:
            output.hoveredwordSpacing || output.wordSpacing || "normal",
          textIndent: output.hoveredtextIndent || output.textIndent || "0",
          whiteSpace: output.hoveredwhiteSpace || output.whiteSpace || "normal",
          textOverflow:
            output.hoveredtextOverflow || output.textOverflow || "clip",
          direction: output.hovereddirection || output.direction || "ltr",
          unicodeBidi:
            output.hoveredunicodeBidi || output.unicodeBidi || "normal",
          writingMode:
            output.hoveredwritingMode || output.writingMode || "horizontal-tb",

          cursor: output.hoveredcursor || output.cursor || "default",
          transform: output.hoveredtransform || output.transform || "none",
          transition: output.hoveredtransition || output.transition || "none",
        }
      : {};
  const activeStyles =
    isDivActive && output.pseudoClass === "active"
      ? {
          // width: output.activatedwidth || output.width || "200px",
          // height: output.activatedheight || output.height || "auto",

          padding: output.activatedpadding || output.padding || "0",
          paddingTop:
            output.activatedpaddingTop || output.activatedpadding || "3px",
          paddingRight:
            output.activatedpaddingRight || output.activatedpadding || "3px",
          paddingBottom:
            output.activatedpaddingBottom || output.activatedpadding || "3px",
          paddingLeft:
            output.activatedpaddingLeft || output.activatedpadding || "3px",
          margin: output.activatedmargin || output.margin || "0px",
          marginTop:
            output.activatedmarginTop || output.activatedmargin || "0px",
          marginRight:
            output.activatedmarginRight || output.activatedmargin || "0px",
          marginBottom:
            output.activatedmarginBottom || output.activatedmargin || "0px",
          marginLeft:
            output.activatedmarginLeft || output.activatedmargin || "8px",

          borderTop:
            output.activatedborderTop ||
            output.activatedborder ||
            output.border ||
            "1px solid #000000",
          borderRight:
            output.activatedborderRight ||
            output.activatedborder ||
            output.border ||
            "1px solid #000000",
          borderBottom:
            output.activatedborderBottom ||
            output.activatedborder ||
            output.border ||
            "1px solid #000000",
          borderLeft:
            output.activatedborderLeft ||
            output.activatedborder ||
            output.border ||
            "1px solid #000000",
          borderSapcing:
            output.activatedborderSpacing || output.borderSpacing || "0px",
          borderCollapse:
            output.activatedborderCollapse || output.borderCollapse || "0px",
          borderRadius:
            output.activatedborderRadius || output.borderRadius || "0",
          outline: output.activatedoutline || output.outline || "none",

          position: output.activatedposition || output.position || "relative",
          top: output.activatedtop || output.top || "auto",
          right: output.activatedright || output.right || "auto",
          bottom: output.activatedbottom || output.bottom || "auto",
          left: output.activatedleft || output.left || "auto",
          zIndex: output.activatedzIndex || output.zIndex || "auto",

          color: output.activatedcolor || output.color || "#000000",
          backgroundColor:
            output.activatedbackgroundColor ||
            output.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (output.activatedbackgroundImage) {
              if (output.activatedbackgroundImage.startsWith("./")) {
                const imageName = output.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${output.activatedbackgroundImage})`;
            }
            if (output.backgroundImage) {
              if (output.backgroundImage.startsWith("./")) {
                const imageName = output.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${output.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            output.activatedbackgroundPosition ||
            output.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            output.activatedbackgroundSize || output.backgroundSize || "auto",
          backgroundRepeat:
            output.activatedbackgroundRepeat ||
            output.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            output.activatedbackgroundAttachment ||
            output.backgroundAttachment ||
            "scroll",

          boxShadow: output.activatedboxShadow || output.boxShadow || "none",
          opacity: output.activatedOpacity || output.opacity || 1,
          display: output.activateddisplay || output.display || "inline",

          fontFamily:
            output.activatedfontFamily || output.fontFamily || "inherit",
          fontSize: output.activatedfontSize || output.fontSize || "inherit",
          fontWeight:
            output.activatedfontWeight || output.fontWeight || "normal",
          fontStyle: output.activatedfontStyle || output.fontStyle || "normal",
          fontVariant:
            output.activatedfontVariant || output.fontVariant || "normal",
          lineHeight:
            output.activatedlineHeight || output.lineHeight || "normal",
          textShadow: output.activatedtextShadow || output.textShadow || "none",
          textAlign: output.activatedtextAlign || output.textAlign || "left",
          verticalAlign:
            output.activatedverticalAlign || output.verticalAlign || "baseline",
          textDecoration:
            output.activatedtextDecoration || output.textDecoration || "none",
          textTransform:
            output.activatedtextTransform || output.textTransform || "none",
          letterSpacing:
            output.activatedletterSpacing || output.letterSpacing || "normal",
          wordSpacing:
            output.activatedwordSpacing || output.wordSpacing || "normal",
          textIndent: output.activatedtextIndent || output.textIndent || "0",
          whiteSpace:
            output.activatedwhiteSpace || output.whiteSpace || "normal",
          textOverflow:
            output.activatedtextOverflow || output.textOverflow || "clip",
          direction: output.activateddirection || output.direction || "ltr",
          unicodeBidi:
            output.activatedunicodeBidi || output.unicodeBidi || "normal",
          writingMode:
            output.activatedwritingMode ||
            output.writingMode ||
            "horizontal-tb",

          cursor: output.activatedcursor || output.cursor || "default",
          transform: output.activatedtransform || output.transform || "none",
          transition: output.activatedtransition || output.transition || "none",
        }
      : {};

  const outputStyle = {
    width: output.width || "200px",
    height: output.height || "auto",

    margin: output.margin || "0px",
    marginTop: output.marginTop || output.margin || "0",
    marginRight: output.marginRight || output.margin || "0px",
    marginBottom: output.marginBottom || output.margin || "0",
    marginLeft: output.marginLeft || output.margin || "8px",
    padding: output.padding || "0px",
    paddingTop: output.paddingTop || output.padding || "3px",
    paddingRight: output.paddingRight || output.padding || "3px",
    paddingBottom: output.paddingBottom || output.padding || "3px",
    paddingLeft: output.paddingLeft || output.padding || "3px",

    borderTop: isHovered
      ? "1px dashed #555"
      : output.borderTop || output.border || "1px solid #000000",
    borderRight: isHovered
      ? "1px dashed #555"
      : output.borderRight || output.border || "1px solid #000000",
    borderBottom: isHovered
      ? "1px dashed #555"
      : output.borderBottom || output.border || "1px solid #000000",
    borderLeft: isHovered
      ? "1px dashed #555"
      : output.borderLeft || output.border || "1px solid #000000",

    borderSapcing: output.borderSpacing || "0px",
    borderCollapse: output.borderCollapse || "0px",
    borderRadius: output.borderRadius || "0",
    outline: output.outline || "none",

    position: output.position || "static",
    top: output.top || "auto",
    right: output.right || "auto",
    bottom: output.bottom || "auto",
    left: output.left || "auto",
    zIndex: output.zIndex || "auto",

    color: output.color || "#000000",
    backgroundColor: output.backgroundColor || "#ffffff",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),
    backgroundPosition:
      output.focusedbackgroundPosition || output.backgroundPosition || "0% 0%",
    backgroundSize:
      output.focusedbackgroundSize || output.backgroundSize || "auto",
    backgroundRepeat:
      output.focusedbackgroundRepeat || output.backgroundRepeat || "repeat",
    backgroundAttachment:
      output.focusedbackgroundAttachment ||
      output.backgroundAttachment ||
      "scroll",

    fontSize: output.fontSize || "inherit",
    fontFamily: output.fontFamily || "inherit",
    fontWeight: output.fontWeight || "normal",
    fontStyle: output.fontStyle || "normal",
    fontVariant: output.fontVariant || "normal",
    lineHeight: output.lineHeight || "normal",
    textAlign: output.textAlign || "left",
    textDecoration: output.textDecoration || "none",
    textTransform: output.textTransform || "none",
    textShadow: output.textShadow || "none",
    verticalAlign: output.verticalAlign || "baseline",
    letterSpacing: output.letterSpacing || "normal",
    wordSpacing: output.wordSpacing || "normal",
    textIndent: output.textIndent || "0",
    whiteSpace: output.whiteSpace || "normal",
    textOverflow: output.textOverflow || "clip",
    direction: output.direction || "ltr",
    unicodeBidi: output.unicodeBidi || "normal",
    writingMode: output.writingMode || "horizontal-tb",

    opacity: output.opacity || "1",
    display: output.display || "inline",
    cursor: output.cursor || "auto",

    transform: output.transform || "none",
    transition: output.transition || "none",

    scale: isHovered ? "1.01" : "1",
    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  return (
    <>
      <output
        htmlFor={output.for}
        name={output.nameX}
        style={outputStyle}
        onMouseEnter={() => setIsDivHovered(true)}
        onMouseLeave={() => setIsDivHovered(false)}
        onFocus={() => setIsDivFocused(true)}
        onBlur={() => setIsDivFocused(false)}
        onMouseDown={() => setIsDivActive(true)}
        onMouseUp={() => setIsDivActive(false)}
      >
        {output.value || "Result"}
      </output>
    </>
  );
};

export default OutputComponent;
